import css from "./standart.module.css";
import { logOut } from "../../lib/logOut";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import { fetchCSRFToken, getCSRFToken } from "../../lib/csrf";
import SubModal from "./SubModal";
import { pollSubscriptionStatus } from "../../lib/subscriptionStatusApi";

const AccountHeader = ({
  authUser,
  pageTheme,
  isAccount,
  activeTab,
  setActiveTab,
  categories,
  selectedCategoryIds,
  onCategoryClick,
  selectedLevel,
  setSelectedLevel,
  allLevels,
  onLevelFilter,
  onClearAllFilters
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 55, left: 352 });
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const languages = [
    { code: "en", label: "EN" },
    { code: "uk", label: "UA" },
    { code: "pl", label: "PL" },
  ];

  const handleNavigate = (path, options = {}) => {
    navigate(path, options);
  };

  const changeLanguage = async (lang) => {
    setLoading(true);
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);

      if (authUser && authUser.isAuthenticated === true) {
        let csrfToken = getCSRFToken();
        if (!csrfToken) {
          await fetchCSRFToken();
          csrfToken = getCSRFToken();
        }

        await axios.patch(
          "/api/v1/user-profile/current",
          { language: lang },
          {
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": csrfToken,
            },
            withCredentials: true,
          }
        );
      }
    } catch (error) {
      console.error("Помилка зміни мови:", error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const success = await logOut();
    if (success) {
      setTimeout(() => {
        window.location.href = "/signin";
      }, 300)

    }
  };

  // Get category by type helper function
  const getCategoryByType = (type) => {
    const categoryMappings = {
      travel: ['travel', 'подорож', 'podróż', 'tourism', 'туризм', 'airports', "аеропорти", "lotniska"],
      work: ['work', 'робота', 'praca', 'job', 'career', 'кар\'єра', 'business', 'бізнес'],
      education: ['education', 'освіта', 'edukacja', 'school', 'школа', 'learning', 'навчання'],
      health: ['health', 'здоров\'я', 'zdrowie', 'medical', 'медичн', 'medicine', 'лікування'],
      tech: ['tech', 'technology', 'технолог', 'news', 'новини', 'wiadomości', 'computer', 'комп\'ютер'],
      leisure: ['leisure', 'дозвілля', 'rozrywka', 'entertainment', 'розваги', 'hobby', 'хобі', 'sport', 'спорт'],
      daily: ['daily', 'щоденн', 'codzienn', 'life', 'життя', 'życie', 'routine', 'побут']
    };

    const keywords = categoryMappings[type] || [];
    const parentCategories = categories.filter(category => category.parent === null);

    return parentCategories.find(cat => {
      const catName = cat.name.toLowerCase();
      return keywords.some(keyword => catName.includes(keyword.toLowerCase()));
    });
  };

  const handleCategoryClick = (categoryId) => {
    if (categoryId === 'all') {
      onClearAllFilters();
      return;
    }
    // Switch to all videos tab when category is clicked
    setActiveTab('all videos');
    onCategoryClick(categoryId);
    setIsMobileMenuOpen(false); // Close mobile menu after selection
  };

  const handleLevelSelect = (levelKey) => {
    // Switch to all videos tab when level is selected
    setActiveTab('all videos');
    onLevelFilter(levelKey);
    setIsLevelDropdownOpen(false);
  };

  // Get dropdown position
  const getDropdownPosition = () => {
    const levelElement = document.querySelector('.nav-category');
    if (levelElement) {
      const rect = levelElement.getBoundingClientRect();
      return {
        top: rect.bottom + 5,
        left: rect.left
      };
    }
    return { top: 55, left: 352 };
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Fetch current subscription when user is authenticated
  const fetchCurrentSubscription = async () => {
    if (!authUser?.isAuthenticated) return;

    let activeSubscription = null;

    // 1) Try orders endpoint first (fast)
    try {
      const response = await axios.get("/api/billing/orders/", {
        withCredentials: true,
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        activeSubscription = response.data.find((sub) => Number(sub.status) === 2) || null;
      }
    } catch (error) {
      console.error("Error fetching current subscription (orders):", error);
    }

    if (activeSubscription) {
      setCurrentSubscription(activeSubscription);
      return;
    }

    // 2) Fallback to subscription/status polling endpoint (authoritative)
    try {
      const result = await pollSubscriptionStatus(5, 2000);
      if (result && typeof result === "object" && Number(result.status) === 2) {
        setCurrentSubscription(result);
      } else {
        setCurrentSubscription(result);
      }
    } catch (error) {
      console.error("Error fetching current subscription (poll):", error);
      setCurrentSubscription(null);
    }
  };

  useEffect(() => {
    fetchCurrentSubscription();
  }, [authUser?.isAuthenticated]);

  // Close mobile menu and dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.hamburger')) {
        setIsMobileMenuOpen(false);
      }
      if (isLevelDropdownOpen && !event.target.closest('.nav-category')) {
        setIsLevelDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isMobileMenuOpen, isLevelDropdownOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Main Header */}
      <nav className={css.navBar}>
        <div className={css.navLeft}>
          <Link to="/" className={css.logo}>
            <div className={css.logoIcon}>AI</div>
            <span>AI Lingua</span>
          </Link>
        </div>

        <div className={css.navRight}>
          <div className={`${css.langSwitcher} ${css.accH}`}>
            {languages.map(({ code, label }) => (
              <button
                key={code}
                className={`${css.langBtn} ${i18n.language === code ? css.active : ''}`}
                onClick={() => changeLanguage(code)}
                disabled={loading}
              >
                {loading && i18n.language === code ? "⏳" : label}
              </button>
            ))}
          </div>

          <button
            className={`${css.pricingBtn} ${css.accH}`}
            onClick={() => navigate('/pricing')}>
            {t("Pricing")}
          </button>

          <button
            className={`${css.subscriptionBtn} ${css.accH}`}
            onClick={() => setIsSubModalOpen(true)}
          >
            Subscription
          </button>

          {authUser && authUser.isAuthenticated === true ? (
            <div className={css.userDropdownWrap}>
              <button 
                className={css.ubtn} 
                onClick={() => setIsProfileOpen(!isProfileOpen)}
              >
                <div className={css.uav}>
                  {authUser?.name ? authUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className={css.uname}>{authUser?.name || 'User'}</span>
              </button>
              
              {isProfileOpen && (
                <div className={css.userDropdownMenu}>
                  <button onClick={() => { setIsProfileOpen(false); handleNavigate('/profile'); }}>
                    {t("profile", "Профіль")}
                  </button>
                  <button onClick={() => { setIsProfileOpen(false); handleNavigate('/'); }}>
                    {t("My learning")}
                  </button>
                  <button onClick={() => { setIsProfileOpen(false); setIsSubModalOpen(true); }}>
                    {t("Subscription")}
                  </button>
                  <div className={css.userDropdownDivider}></div>
                  <button onClick={handleLogout} className={css.logoutText}>
                    {t("logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className={`${css.btnPrimary} ${css.accH}`} onClick={() => handleNavigate("/signin")}>
              {t("Log In")}
            </button>
          )}

          {/* Mobile Hamburger Menu */}
          <div className={`${css.hamburger} hamburger ${isMobileMenuOpen ? css.active : ''}`} onClick={toggleMobileMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>

      {/* Navigation Categories - Below Header */}
      <div className={css.navCategoriesContainer}>
        <div className={css.navCategories}>
          {/* My Learning */}
          <div
            className={`${css.navCategory} ${activeTab === 'my learning' ? css.active : ''}`}
            onClick={() => setActiveTab('my learning')}
          >
            <span className={css.icon}>🏠</span>
            <span>{t("My learning")}</span>
          </div>

          {/* Language Level */}
          <div
            className={`${css.navCategory} nav-category`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isLevelDropdownOpen) {
                const position = getDropdownPosition();
                setDropdownPosition(position);
              }
              setIsLevelDropdownOpen(!isLevelDropdownOpen);
            }}
          >
            <span className={css.icon}>📚</span>
            <span>{t("Language level")}</span>
            <span className={css.dropdownArrow}>▼</span>
            {isLevelDropdownOpen && (
              <div
                className={css.levelDropdown}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  zIndex: 1000
                }}
              >
                <div className={css.levelDropdownItem} onClick={(e) => {
                  e.stopPropagation();
                  handleLevelSelect(null);
                }}>
                  {t("All Levels")}
                </div>
                <div className={css.levelDropdownItem} onClick={(e) => {
                  e.stopPropagation();
                  handleLevelSelect("1,2");
                }}>
                  {t("Beginner")} (A1-A2)
                </div>
                <div className={css.levelDropdownItem} onClick={(e) => {
                  e.stopPropagation();
                  handleLevelSelect("3,4");
                }}>
                  {t("Intermediate")} (B1-B2)
                </div>
                <div className={css.levelDropdownItem} onClick={(e) => {
                  e.stopPropagation();
                  handleLevelSelect("5,6");
                }}>
                  {t("Advanced")} (C1-C2)
                </div>
              </div>
            )}
          </div>

          {/* AI Videos */}
          <div
            className={`${css.navCategory} ${activeTab === 'all videos' ? css.active : ''}`}
            onClick={() => handleNavigate('/allvideos')}
          >
            <span className={css.icon}>🎬</span>
            <span>{t("All Videos")}</span>
          </div>

          {/* Category Navigation Items */}
          {(() => {
            const dailyCategory = getCategoryByType('daily');
            return dailyCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(dailyCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(dailyCategory.id)}
              >
                <span className={css.icon}>📰</span>
                <span>{t("Daily Life")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const workCategory = getCategoryByType('work');
            return workCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(workCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(workCategory.id)}
              >
                <span className={css.icon}>💼</span>
                <span>{t("Work")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const travelCategory = getCategoryByType('travel');
            return travelCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(travelCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(travelCategory.id)}
              >
                <span className={css.icon}>✈️</span>
                <span>{t("Travel")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const healthCategory = getCategoryByType('health');
            return healthCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(healthCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(healthCategory.id)}
              >
                <span className={css.icon}>❤️</span>
                <span>{t("Health")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const educationCategory = getCategoryByType('education');
            return educationCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(educationCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(educationCategory.id)}
              >
                <span className={css.icon}>🎓</span>
                <span>{t("Education")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const techCategory = getCategoryByType('tech');
            return techCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(techCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(techCategory.id)}
              >
                <span className={css.icon}>📱</span>
                <span>{t("News & Tech")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const leisureCategory = getCategoryByType('leisure');
            return leisureCategory ? (
              <div
                className={`${css.navCategory} ${selectedCategoryIds.includes(leisureCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(leisureCategory.id)}
              >
                <span className={css.icon}>🎮</span>
                <span>{t("Leisure")}</span>
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`${css.mobileMenuOverlay} ${isMobileMenuOpen ? css.active : ''}`} onClick={closeMobileMenu}></div>

      {/* Mobile Menu */}
      <div className={`${css.mobileMenu} mobile-menu ${isMobileMenuOpen ? css.active : ''}`}>
        <div className={css.mobileMenuHeader}>
          <div className={css.logo}>
            <div className={css.logoIcon}>AI</div>
            <span>AI Lingua</span>
          </div>
          <button className={css.mobileMenuClose} onClick={closeMobileMenu}>×</button>
        </div>

        <div className={css.mobileNavCategories}>
          {/* My Learning */}
          <div
            className={`${css.mobileNavCategory} ${activeTab === 'my learning' ? css.active : ''}`}
            onClick={() => {
              setActiveTab('my learning');
              closeMobileMenu();
            }}
          >
            <span className={css.icon}>🏠</span>
            <span>{t("My learning")}</span>
          </div>

          {/* Language Level */}
          <div className={css.mobileNavCategory}>
            <span className={css.icon}>📚</span>
            <span>{t("Language level")}</span>
          </div>
          <div className={css.mobileLevelOptions}>
            <div className={css.mobileNavCategory} onClick={() => handleLevelSelect(null)}>
              <span className={css.icon}>🌐</span>
              <span>{t("All Levels")}</span>
            </div>
            <div className={css.mobileNavCategory} onClick={() => handleLevelSelect("1,2")}>
              <span className={css.icon}>🟢</span>
              <span>{t("Beginner")} (A1-A2)</span>
            </div>
            <div className={css.mobileNavCategory} onClick={() => handleLevelSelect("3,4")}>
              <span className={css.icon}>🟡</span>
              <span>{t("Intermediate")} (B1-B2)</span>
            </div>
            <div className={css.mobileNavCategory} onClick={() => handleLevelSelect("5,6")}>
              <span className={css.icon}>🔴</span>
              <span>{t("Advanced")} (C1-C2)</span>
            </div>
          </div>

          {/* AI Videos */}
          <div
            className={`${css.mobileNavCategory} ${activeTab === 'all videos' ? css.active : ''}`}
            onClick={() => {
              handleNavigate('/allvideos');
              closeMobileMenu();
            }}
          >
            <span className={css.icon}>🎬</span>
            <span>{t("All Videos")}</span>
          </div>

          {/* Category Navigation Items */}
          {(() => {
            const dailyCategory = getCategoryByType('daily');
            return dailyCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(dailyCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(dailyCategory.id)}
              >
                <span className={css.icon}>📰</span>
                <span>{t("Daily Life")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const workCategory = getCategoryByType('work');
            return workCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(workCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(workCategory.id)}
              >
                <span className={css.icon}>💼</span>
                <span>{t("Work")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const travelCategory = getCategoryByType('travel');
            return travelCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(travelCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(travelCategory.id)}
              >
                <span className={css.icon}>✈️</span>
                <span>{t("Travel")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const healthCategory = getCategoryByType('health');
            return healthCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(healthCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(healthCategory.id)}
              >
                <span className={css.icon}>❤️</span>
                <span>{t("Health")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const educationCategory = getCategoryByType('education');
            return educationCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(educationCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(educationCategory.id)}
              >
                <span className={css.icon}>🎓</span>
                <span>{t("Education")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const techCategory = getCategoryByType('tech');
            return techCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(techCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(techCategory.id)}
              >
                <span className={css.icon}>📱</span>
                <span>{t("News & Tech")}</span>
              </div>
            ) : null;
          })()}

          {(() => {
            const leisureCategory = getCategoryByType('leisure');
            return leisureCategory ? (
              <div
                className={`${css.mobileNavCategory} ${selectedCategoryIds.includes(leisureCategory.id) ? css.active : ''}`}
                onClick={() => handleCategoryClick(leisureCategory.id)}
              >
                <span className={css.icon}>🎮</span>
                <span>{t("Leisure")}</span>
              </div>
            ) : null;
          })()}
        </div>

        <div className={css.mobileNavActions}>
          <div className={css.mobileLangSwitcher}>
            {languages.map(({ code, label }) => (
              <button
                key={code}
                className={`${css.mobileLangBtn} ${i18n.language === code ? css.active : ''}`}
                onClick={() => changeLanguage(code)}
                disabled={loading}
              >
                {loading && i18n.language === code ? "⏳" : label}
              </button>
            ))}
          </div>

          <button
            className={`${css.subscriptionBtn} ${css.accMenuB}`}
            onClick={() => {
              setIsSubModalOpen(true);
              closeMobileMenu();
            }}
          >
            {t("Subscription")}
          </button>

          <button
            className={css.pricingBtn}
            onClick={() => navigate('/pricing')}>
            {t("Pricing")}
          </button>


          {authUser && authUser.isAuthenticated === true ? (
            <button className={css.mobileLogoutBtn} onClick={handleLogout}>
              {t("Log Out")}
            </button>
          ) : (
            <button className={css.mobileLogoutBtn} onClick={() => handleNavigate("/signin")}>
              {t("Log In")}
            </button>
          )}
        </div>
      </div>

      {/* Subscription Modal */}
      <SubModal
        isOpen={isSubModalOpen}
        onClose={() => setIsSubModalOpen(false)}
        authUser={authUser}
        currentSubscription={currentSubscription}
        onSubscriptionCancelled={fetchCurrentSubscription}
      />
    </>
  );
};

export default AccountHeader;
